// getFeedQueryObject.js
'use strict';

import {
  LIVESTREAM_FEED,
  LIVESTREAM_FEED_SORTINGS,
  LIVESTREAM_FEED_SORTINGS_V2,
} from '../RemoteConfigKeys.js';

import getOperationData from '../selector/getOperationData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getFilterType from '../selector/getFilterType.js';
import getFilterGroupMap from '../selector/getFilterGroupMap.js';

import createCachedSelector from '../resource/createCachedSelector.js';
import {
  OPTION_UNSET,
  QueryType,
  QueryPrefix,
} from '../resource/feedConstants.js';
import { parseQueryOptions, parseOptionsV2 } from '../resource/feedUtils.js';

const defaultObject = {};
const defaultArray = [];

/**
 * Get selected option object which contain feed name, queries and sorts.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} baseFeedName - base feed name.
 * @param {array} selectedQueryOptions - selected query options.
 * @param {string} defaultSorting - default sorting.
 * @return {object} Return parsed selected option object.
{
  intersectedFeedName:
    'user_creators&user_following_ME&user_livestream-v2&user_livestream-v2-filtered',
  intersectedFeedNames: [
    'user_creators',
    'user_following_ME',
    'user_livestream-v2',
    'user_livestream-v2-filtered',
  ],
  filters: ['preset:preview', 'preset:sd', 'region:asia', 'region:naeu'],
  sorts: [],
  feedNameWithQuery: 'user_creators&user_livestream-v2?filters=preset:preview'
}
 */
export const getSelectedQueryOptionObject = createCachedSelector(
  (state, baseFeedName) => baseFeedName,
  (state, baseFeedName, selectedQueryOptions) => selectedQueryOptions,
  (state, baseFeedName, selectedQueryOptions, defaultSorting) => defaultSorting,
  state => getFilterType(state),
  (
    baseFeedName = '',
    selectedQueryOptions = defaultArray,
    defaultSorting,
    filterType = 'v1'
  ) => {
    let tasks = [...selectedQueryOptions];

    let result = {};
    switch (filterType) {
      case 'v1':
        // If there's no sorting and default sorting exists, add default sorting
        if (
          !tasks.some(option => option.includes('sorting=')) &&
          defaultSorting
        ) {
          tasks.push(defaultSorting);
        }

        result = parseQueryOptions({ options: [baseFeedName, ...tasks] });
        break;
      case 'v2': {
        // If there's no sorting and default sorting exists, add default sorting
        if (
          !tasks.some(option => option.includes('sorting=')) &&
          defaultSorting
        ) {
          tasks.push(`${LIVESTREAM_FEED_SORTINGS_V2}/${defaultSorting}`);
        }

        result = parseOptionsV2({
          baseFeedName,
          options: tasks,
        });
        break;
      }
    }

    return result;
  }
)(
  (state, baseFeedName, selectedQueryOptions, defaultSorting, filterType) =>
    `${baseFeedName}:${selectedQueryOptions}:${defaultSorting}:${filterType}`
);

/**
 * Get parsed selected draft option object which contain feed name, queries and sorts.
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} baseFeedName - base feed name.
 * @param {boolean} optionKey - selected option key.
 * @return {object|undefined} Return parsed selected option object.
{
  intersectedFeedName:
    'user_creators&user_following_ME&user_livestream-v2&user_livestream-v2-filtered',
  intersectedFeedNames: [
    'user_creators',
    'user_following_ME',
    'user_livestream-v2',
    'user_livestream-v2-filtered',
  ],
  filters: ['preset:preview', 'preset:sd', 'region:asia', 'region:naeu'],
  sorts: [],
  feedNameWithQuery: 'user_creators&user_livestream-v2?filters=preset:preview'
}
 */
export const getSelectedDraftQueryOptionObject = createCachedSelector(
  (state, baseFeedName) => baseFeedName,
  (state, baseFeedName) =>
    getOperationData(
      state,
      ['feed', baseFeedName, QueryType.FILTER],
      'draftOptions'
    ),
  (state, baseFeedName) => {
    const draftOptions = getOperationData(
      state,
      ['feed', baseFeedName, QueryType.SORT],
      'draftOptions'
    );

    return draftOptions?.length
      ? draftOptions
      : [getRemoteConfigData(state, LIVESTREAM_FEED_SORTINGS)?.[0]]; // If there's no sorting and default sorting exists, add default sorting
  },
  (
    baseFeedName,
    selectedFilterOptions = defaultArray,
    selectedSortOptions = defaultArray
  ) => {
    let result = {
      intersectedFeedName: '',
      intersectedFeedNames: [],
      filters: [],
      sorts: [],
      feedNameWithQuery: '',
    };
    if (!baseFeedName) {
      return result;
    }
    const tasks = [
      baseFeedName,
      ...selectedFilterOptions,
      ...selectedSortOptions,
    ];
    result = parseQueryOptions({ options: tasks });
    return result;
  }
)((state, baseFeedName) => `${baseFeedName}`);

/**
 * Select query option object by remote config key name and query type
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} remoteConfigKeyName - remote config key name.
 * @param {QueryType|undefined} queryType - query type. `undefined` means all query types.
 * @return {object|undefined} Return group object. ex:
{
  "LIVESTREAM_FEED_FILTERS_REGIONS": [
    "unset",
    "user_livestream-v2&user_creator?filters=region:asia",
    "user_livestream-v2&user_creator?filters=region:naeu"
  ],
  "LIVESTREAM_FEED_FILTERS_FOLLOWS": [
    "unset",
    "user_livestream-v2&user_following_ME"
  ]
}
 */
export const getCandidateQueryOptionObject = createCachedSelector(
  (state, remoteConfigKeyName) => remoteConfigKeyName,
  (state, remoteConfigKeyName, queryType) => queryType,
  state => state.get('remoteConfig'),
  state => getFilterType(state),
  state => getFilterGroupMap(state, getFilterType(state)),
  (
    remoteConfigKeyName,
    queryType,
    remoteConfigMap,
    filterType = 'v1',
    filterGroupMap = defaultObject
  ) => {
    if (!remoteConfigKeyName) {
      return;
    }

    let result = {};
    switch (filterType) {
      case 'v1': {
        if (remoteConfigKeyName === LIVESTREAM_FEED) {
          result = Object.entries(filterGroupMap).reduce(
            (accumulator, [key, options]) => {
              switch (queryType) {
                case QueryType.FILTER:
                  if (!key.includes(QueryPrefix[QueryType.FILTER])) {
                    return accumulator;
                  }
                  break;
                case QueryType.SORT:
                  if (!key.includes(QueryPrefix[QueryType.SORT])) {
                    return accumulator;
                  }
                  break;
                default:
                  break;
              }

              if (options.length > 0) {
                accumulator[key] = [OPTION_UNSET, ...options];
              }

              return accumulator;
            },
            {}
          );
        } else {
          // Using ${key}_${prefix} to get the options
          result = remoteConfigMap
            .filter((value, key) => {
              if (!value) {
                return false;
              }
              if (!queryType) {
                return Object.values(QueryPrefix).some(prefix =>
                  key.startsWith(`${remoteConfigKeyName}_${prefix}`)
                );
              }
              return key.startsWith(
                `${remoteConfigKeyName}_${QueryPrefix[queryType]}`
              );
            })
            .reduce((accumulator, value, key) => {
              accumulator[key] = [
                OPTION_UNSET,
                ...(remoteConfigMap?.get(key)?.split(' ') || []),
              ];
              return accumulator;
            }, {});
        }
        break;
      }
      case 'v2': {
        result = filterGroupMap;
        break;
      }
    }

    return result;
  }
)(
  (state, remoteConfigKeyName, queryType) =>
    `${remoteConfigKeyName}:${queryType}`
);
